import React, { useCallback, useMemo, useState, type PropsWithChildren, Fragment } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import DateTimeEditViewEntryPoint from '@atlassian/jira-issue-field-date-time-editview-full/src/entrypoint.tsx';
import type { DateTimeEditViewProps } from '@atlassian/jira-issue-field-date-time-editview-full/src/ui/date-time/types.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { DateTimeReadView } from '@atlassian/jira-issue-field-date-time-readview-full/src/ui/date-time/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { standardizeLocale } from '@atlassian/jira-issue-format-date/src/common/utils.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createMutationErrorHandlers } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/utils/extract-error-message.tsx';
import type { dateTime_issueFieldDateTimeInlineEditFull_DateTimeInlineEditViewWithIsEditable_fragmentRef$key as DateTimeWithIsEditableFragment } from './__generated__/dateTime_issueFieldDateTimeInlineEditFull_DateTimeInlineEditViewWithIsEditable_fragmentRef.graphql';
import type { dateTime_issueFieldDateTimeInlineEditFull_DateTimeInlineEditView_fragmentRef$key as DateTimeFragment } from './__generated__/dateTime_issueFieldDateTimeInlineEditFull_DateTimeInlineEditView_fragmentRef.graphql';
import type { dateTime_issueFieldDateTime_DateTimeField_Mutation as DateTimeMutation } from './__generated__/dateTime_issueFieldDateTime_DateTimeField_Mutation.graphql';
import { getFormattedDate, getNext30MinuteInterval } from './utils.tsx';
import type {
	JiraDateTimePickerValue,
	DateTimeInlineEditViewProps,
	DateTimeInlineEditViewWithIsEditableProps,
} from './types.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-date-time-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `DateTimeInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [DateTimeInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const DateTimeInlineEditViewWithIsEditable = ({
	attributes,
	spacing = 'compact',
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupWidth,
	editViewPopupTarget,
	fragmentRef,
	menuPosition,
	menuPortalTarget,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	isEditable,
	readViewFitContainerHeight,
	timeZone,
	isAutoFocusFixEnabled,
	readViewDateOnlyFormat,
	shouldRenderHoverPopover,
}: DateTimeInlineEditViewWithIsEditableProps): React.JSX.Element => {
	// #region Relay
	const data = useFragment<DateTimeWithIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment dateTime_issueFieldDateTimeInlineEditFull_DateTimeInlineEditViewWithIsEditable_fragmentRef on JiraDateTimePickerField {
				...dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView
				id
				fieldId
				type
				name
				dateTime
			}
		`,
		fragmentRef,
	);
	const { id: uniqueFieldId, fieldId, name: fieldName, type, dateTime } = data;
	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<DateTimeMutation>(graphql`
		mutation dateTime_issueFieldDateTime_DateTimeField_Mutation(
			$input: JiraUpdateDateTimeFieldInput!
		) @raw_response_type {
			jira {
				updateDateTimeField(input: $input) {
					success
					errors {
						message
					}
					field {
						dateTime
					}
				}
			}
		}
	`);
	// #endregion

	// #region Common state
	const { overriding } = useInlineEditFieldInjections();
	const locale = useLocale();
	const initialValue: JiraDateTimePickerValue = dateTime ?? null;
	const [updatedValue, setUpdatedValue] = useState<JiraDateTimePickerValue>(initialValue);
	const isFieldEditable = useMemo(
		() => overriding.overrideIsEditable(isEditable),
		[isEditable, overriding],
	);
	// #endregion

	// #region Handle new value
	const handleSubmit = useCallback(
		(newDateTime: JiraDateTimePickerValue, { onSuccess, onFail }: OnSubmitCallbacks) => {
			onSubmit?.(newDateTime);

			const onCompleted = fg('thor_inline-edit-actions-error-extraction')
				? createMutationErrorHandlers({ onSuccess, onFail })
				: // eslint-disable-next-line @typescript-eslint/no-explicit-any
					(response: any) => {
						if (response.jira?.updateDateTimeField?.success) {
							onSuccess();
						} else {
							onFail();
						}
					};

			commit({
				variables: {
					input: {
						id: uniqueFieldId,
						operation: {
							datetime: newDateTime ?? null,
							operation: 'SET',
						},
					},
				},
				onCompleted,
				onError(error) {
					onFail(error);
				},
				optimisticResponse: {
					jira: {
						updateDateTimeField: {
							success: true,
							errors: null,
							field: {
								id: uniqueFieldId,
								dateTime: newDateTime,
							},
						},
					},
				},
			});
		},
		[commit, onSubmit, uniqueFieldId],
	);

	const {
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChange,
		handleCopy,
		handleCut,
		handlePaste,
		invalidMessage,
		isEditing,
	} = useFieldInlineEditActions({
		attributes,
		fieldId,
		fieldName,
		fieldType: type,
		initialValue,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		updatedValue,
		clipboardComponentType: 'dateTime',
	});

	// #region Read view
	const renderReadView = useCallback(
		() => (
			<MaybeUFOSegment>
				<DateTimeReadView
					fragmentRef={data}
					timeZone={timeZone}
					isDateOnlyFormat={readViewDateOnlyFormat}
					shouldRenderHoverPopover={shouldRenderHoverPopover}
				/>
			</MaybeUFOSegment>
		),
		[data, timeZone, readViewDateOnlyFormat, shouldRenderHoverPopover],
	);
	// #endregion
	// #region Edit view
	const timePlaceholder = useMemo(() => {
		const standardizedLocale = standardizeLocale(locale);
		return getNext30MinuteInterval(undefined, standardizedLocale);
	}, [locale]);

	const datePlaceholder = useMemo(() => {
		let formattedDate = '';

		try {
			formattedDate = getFormattedDate(locale);
		} catch (dateFormatError) {
			fireErrorAnalytics({
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				error: dateFormatError as Error,
				meta: {
					packageName: 'dateTimeFieldInlineEdit',
					id: 'formatDateError',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		return formattedDate;
	}, [locale]);
	const getEditViewProps = (fieldProps: ValidationFieldProps): DateTimeEditViewProps => ({
		...fieldProps,
		autoFocus: isAutoFocusFixEnabled,
		value: updatedValue,
		menuPosition,
		menuPortalTarget,
		onChange: handleChange,
		timeZone,
		...(expValEquals('jira_issue_view_date_time_picker_experiment_new', 'isEnabled', true)
			? {
					autoFocus: true,
					spacing,
					datePlaceholder,
					timePlaceholder,
				}
			: {}),
	});
	// #endregion

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewPopupTarget={editViewPopupTarget}
			editViewPopupWidth={editViewPopupWidth}
			editViewEntryPoint={DateTimeEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			hideActionButtons
		/>
	);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [DateTimeInlineEditViewProps](./types.tsx)
 */

export const DateTimeInlineEditView = ({
	fragmentRef,
	...props
}: DateTimeInlineEditViewProps): React.JSX.Element => {
	const data = useFragment<DateTimeFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment dateTime_issueFieldDateTimeInlineEditFull_DateTimeInlineEditView_fragmentRef on JiraDateTimePickerField {
				...dateTime_issueFieldDateTimeInlineEditFull_DateTimeInlineEditViewWithIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);

	return (
		<DateTimeInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
